import { Plus, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useRemoveMember } from '../api'
import { PROJECT_TEAM_PERM } from '../permissions'
import type { Project, TeamMember } from '../types'
import { toastError, useStaffLookup } from '../ui'
import { MemberFormModal } from './MemberFormModal'
import { DisciplineBadge } from './ProjectBits'
import { RowMenu } from './RowMenu'

export function TeamPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { nameOf } = useStaffLookup()
  const remove = useRemoveMember(project.id)
  const [dialog, setDialog] = useState<{ open: boolean; member: TeamMember | null }>({ open: false, member: null })
  const canWrite = can(PROJECT_TEAM_PERM)

  const onRemove = async (m: TeamMember) => {
    const name = nameOf(m.userId)
    const owned = project.tasks.filter((x) => x.assigneeId === m.userId && x.status !== 'done').length
    const ok = await confirmModal({
      title: t('projects.team.removeTitle'),
      description: t('projects.team.removeBody', { name, project: project.name, count: owned }),
      confirmLabel: t('projects.team.remove'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(m.userId)
      toast.success(t('projects.team.removed'))
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <section className="flex min-w-0 flex-col">
      <header className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
        <h3 className="font-semibold">{t('projects.team.title', { count: project.team.length })}</h3>
        {canWrite ? (
          <Button variant="outline" size="sm" onClick={() => setDialog({ open: true, member: null })}>
            <Plus />
            {t('projects.team.assign')}
          </Button>
        ) : null}
      </header>
      {project.team.length === 0 ? (
        <EmptyState icon={UserPlus} title={t('projects.team.empty')} description={t('projects.team.emptyHint')} />
      ) : (
        <ul className="divide-y px-4 pb-2 sm:px-5">
          {project.team.map((m) => {
            const name = nameOf(m.userId)
            return (
              <li key={m.id} className="flex items-center gap-3 py-3">
                <Avatar name={name} className="size-10" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{name}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.title}</p>
                </div>
                <DisciplineBadge discipline={m.discipline} />
                {canWrite ? (
                  <RowMenu label={name} onEdit={() => setDialog({ open: true, member: m })} onDelete={() => void onRemove(m)} deleteLabel={t('projects.team.remove')} />
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      <MemberFormModal open={dialog.open} project={project} member={dialog.member} onOpenChange={(open) => setDialog((s) => ({ ...s, open }))} />
    </section>
  )
}
